/** bonddoc.ts — 법인별 사채 문서(청약서·인수증 / 인수계약서) 생성·버전 관리
 *  docgen.ts와 동일 패턴이나 복합키 `bond_{coId}_{key}` + 법인명 기준 파일명 + genBondDocs 엔드포인트 사용
 */
import { genBondDocs, BondDocResult } from './api';
import type { Project, CoCard, DocVer, DocVerMap, Master } from './types';

export const BONDDOCS: Record<string, { title: string; seal?: boolean }> = {
  bondsub: { title: '사채청약서·인수증', seal: true },
  bondcontract: { title: '전환사채 인수계약서', seal: true },
  bondceo: { title: '전환사채 인수계약서(대표자 재인수)', seal: true },   // 14단계 투자확인서(대표자 재인수) 첨부 — 자동 생성 후 서명 (2026-09-24)
};
export const BOND_KEYS: string[] = ['bondsub', 'bondcontract'];

export function bondKey(coId: string, key: string): string { return 'bond_' + coId + '_' + key; }
function coName(co: CoCard): string { return ((co.name || '법인').replace(/[\\/:*?<>|]/g, ' ').trim()) || '법인'; }
export function bondFileName(co: CoCard, key: string, ext: string): string {
  return BONDDOCS[key].title + '_' + coName(co) + '.' + ext;
}
export function bondAutoV1(co: CoCard, key: string): DocVer {
  return { label: '자동 생성 v1', doc: bondFileName(co, key, 'docx'), pdf: bondFileName(co, key, 'pdf'), kind: 'auto' };
}
/** 저장본 없거나 자동 v1뿐이면 현재 법인명으로 갱신해 반환 */
export function getBondVers(p: Project, co: CoCard, key: string): DocVer[] {
  const dv = p.docver && p.docver[bondKey(co.id, key)];
  if (dv && dv.length) {
    if (dv.length === 1 && dv[0].kind === 'auto' && !dv[0].s3doc && !dv[0].s3pdf) return [bondAutoV1(co, key)];
    return dv;
  }
  return [bondAutoV1(co, key)];
}

function applyBond(dv: DocVerMap, ck: string, r: BondDocResult, co: CoCard, key: string): DocVer {
  const list = dv[ck] && dv[ck].length ? dv[ck] : [bondAutoV1(co, key)];
  const onlyFake = list.length === 1 && list[0].kind === 'auto' && !list[0].s3doc;
  const ver: DocVer = {
    label: onlyFake ? '자동 생성 v1' : '재생성 v' + (list.length + 1),
    doc: r.doc, pdf: r.pdf, kind: 'auto', s3doc: r.s3doc, s3pdf: r.s3pdf, at: new Date().toISOString(),
  };
  dv[ck] = onlyFake ? [ver] : [...list, ver];
  return ver;
}

/** 법인 사채문서 키 순차 실생성 → docver 패치 */
export async function regenBond(
  p: Project, co: CoCard, keys: string[],
  onPatch: (patch: Partial<Project>) => void, toast: (m: string) => void,
): Promise<DocVerMap> {
  const master = p.master as Master | undefined;
  if (!master) { toast('마스터 정보가 없습니다 — 공통정보를 먼저 입력하세요'); throw new Error('no master'); }
  const dv = JSON.parse(JSON.stringify(p.docver || {})) as DocVerMap;
  toast((co.name || '법인') + ' 사채서류 생성 시작 · ' + keys.length + '건 (건당 2~4초)');
  let fail = 0;
  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];
    try {
      const { results } = await genBondDocs(p.id, co.id, [key], master);
      applyBond(dv, bondKey(co.id, key), results[0], co, key);
      onPatch({ docver: JSON.parse(JSON.stringify(dv)) as DocVerMap });
      toast('생성 ' + (i + 1) + '/' + keys.length + ' · ' + BONDDOCS[key].title + (results[0].converted ? '' : ' (⚠ PDF 변환 실패)'));
    } catch (e) {
      fail++;
      toast('생성 실패 · ' + BONDDOCS[key].title + ' — ' + (e as Error).message);
    }
  }
  toast(fail ? '완료 · 실패 ' + fail + '건은 다시 시도하세요' : (co.name || '법인') + ' 사채서류 생성 완료');
  return dv;
}

/** 현재 버전이 실파일이 아니면 1건 생성 후 그 버전 반환 */
export async function ensureBondReal(
  p: Project, co: CoCard, key: string,
  onPatch: (patch: Partial<Project>) => void, toast: (m: string) => void,
): Promise<DocVer> {
  const vers = getBondVers(p, co, key);
  const cur = vers[vers.length - 1];
  if (cur.s3doc || cur.s3pdf) return cur;
  const dv = await regenBond(p, co, [key], onPatch, toast);
  const list = dv[bondKey(co.id, key)];
  return list[list.length - 1];
}
